/**
 * Line items section (bill composer, step 4).
 *
 * Visual + interaction parity with the invoice composer's
 * `features/invoices/line-items-table.tsx`:
 *   • rounded outer border + overflow-x-auto wrapper, single thead row.
 *   • cells render bare `<input>` with `bg-transparent` + focus-reveal
 *     (`focus:bg-raised focus:ring-1 focus:ring-line`).
 *   • Unit + GST use `Select` from `@flint/ui` (shadcn-style).
 *   • Amount column is computed read-only mono text.
 *   • "Add line item" lives in `<tfoot>` with a raised background.
 *
 * Bill-only additions kept from the prototype:
 *   • flagged rows get a subtle warning background.
 *   • AI discrepancy hint banner below the table inside the same card.
 *
 * Form state stays string-typed (per `lib/form-schema.ts`) — we don't
 * convert to bigint/millis here because the form-schema → API mapping
 * does that at submit time.
 *
 * @see apps/web/src/features/invoices/line-items-table.tsx
 * @see prototype/purchases/bill-new.html §"4 · Line items"
 */
import { Plus, Sparkles, Trash2 } from "lucide-react";
import type { ChangeEvent } from "react";
import { Button, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@flint/ui";
import type { BillLineItem, FormErrors } from "./types.js";
import { computeLineSubtotalPaise } from "./bill-math.js";
import { SectionCard } from "./section-card.js";
import { paiseToInrExact } from "../../../lib/format.js";
import { BillItemPicker, type PickedBillItemValues } from "./bill-item-picker.js";

interface LineItemsSectionProps {
  rows: BillLineItem[];
  error?: FormErrors["lineItems"] | undefined;
  onRowsChange: (rows: BillLineItem[]) => void;
}

const GST_OPTIONS = ["0", "0.25", "3", "5", "12", "18", "28", "40"] as const;
const UNIT_OPTIONS = ["pcs", "Nos", "kg", "m", "box", "set", "hr", "day", "lot"] as const;

// Shared cell-input class — copied from invoice's LineItemsTable so the two
// composers look identical. text-ink-2 inputs, transparent bg, focus reveals
// a raised tile + line ring.
const cellInput =
  "w-full rounded bg-transparent px-1 py-1 text-sm text-ink-2 " +
  "focus:bg-raised focus:outline-none focus:ring-1 focus:ring-line";
const cellInputMono = "font-mono";
const cellInputRight = "text-right";

export function LineItemsSection({ rows, error, onRowsChange }: LineItemsSectionProps) {
  const updateRow = (id: string, patch: Partial<BillLineItem>) => {
    onRowsChange(rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const addRow = () => onRowsChange([...rows, newEmptyRow(rows.length + 1)]);
  const removeRow = (id: string) => onRowsChange(rows.filter((row) => row.id !== id));

  return (
    <SectionCard
      step="4"
      title="Line items"
      description="What you're being charged for. Edit AI extractions in place."
      action={
        <Button intent="ghost" size="sm" onClick={addRow}>
          <Sparkles className="h-4 w-4" />
          Re-extract
        </Button>
      }
    >
      <div className="overflow-x-auto rounded-md border border-line">
        <table className="w-full min-w-[820px] text-sm">
          <thead className="bg-raised text-sm uppercase tracking-wide text-muted">
            <tr>
              <th scope="col" className="px-3 py-2 text-left font-medium">
                Description
              </th>
              <th scope="col" className="w-[88px] px-2 py-2 text-left font-medium">
                HSN
              </th>
              <th scope="col" className="w-[80px] px-2 py-2 text-right font-medium">
                Qty
              </th>
              <th scope="col" className="w-[88px] px-2 py-2 text-left font-medium">
                Unit
              </th>
              <th scope="col" className="w-[110px] px-2 py-2 text-right font-medium">
                Rate (₹)
              </th>
              <th scope="col" className="w-[80px] px-2 py-2 text-right font-medium">
                Disc%
              </th>
              <th scope="col" className="w-[100px] px-2 py-2 text-left font-medium">
                GST
              </th>
              <th scope="col" className="w-[120px] px-2 py-2 text-right font-medium">
                Amount (₹)
              </th>
              <th scope="col" className="w-[40px] px-2 py-2">
                <span className="sr-only">Remove</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const flagged = Boolean(row.priceFlag);
              return (
                <tr key={row.id} className={`border-t border-line ${flagged ? "bg-warn-50" : ""}`}>
                  <td className="px-2 py-2">
                    <div className="flex items-center gap-1">
                      <BillItemPicker
                        onPick={(values) => applyPickedItem(row.id, values, updateRow)}
                      />
                      <input
                        type="text"
                        aria-label={`Description ${row.id}`}
                        value={row.description}
                        onChange={onText(row.id, "description", updateRow)}
                        placeholder="Pick item or type description…"
                        className={`min-w-0 flex-1 ${cellInput}`}
                      />
                    </div>
                  </td>
                  <td className="px-1 py-2">
                    <input
                      type="text"
                      inputMode="numeric"
                      aria-label={`HSN ${row.id}`}
                      value={row.hsn}
                      onChange={onText(row.id, "hsn", updateRow, {
                        normalize: (v) => v.replace(/\D/g, "").slice(0, 8),
                      })}
                      placeholder="0000"
                      className={`${cellInput} ${cellInputMono}`}
                    />
                  </td>
                  <td className="px-1 py-2">
                    <input
                      type="text"
                      inputMode="decimal"
                      aria-label={`Quantity ${row.id}`}
                      value={row.qty}
                      onChange={onText(row.id, "qty", updateRow)}
                      className={`${cellInput} ${cellInputMono} ${cellInputRight}`}
                    />
                  </td>
                  <td className="px-1 py-2">
                    <Select value={row.unit} onValueChange={(v) => updateRow(row.id, { unit: v })}>
                      <SelectTrigger className="h-8 text-sm" aria-label={`Unit ${row.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {UNIT_OPTIONS.map((u) => (
                          <SelectItem key={u} value={u}>
                            {u}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-1 py-2">
                    <input
                      type="text"
                      inputMode="decimal"
                      aria-label={`Rate ${row.id}`}
                      value={row.rate}
                      onChange={onText(row.id, "rate", updateRow)}
                      className={`${cellInput} ${cellInputMono} ${cellInputRight}`}
                    />
                  </td>
                  <td className="px-1 py-2">
                    <input
                      type="text"
                      inputMode="decimal"
                      aria-label={`Discount ${row.id}`}
                      value={row.discountRate}
                      onChange={onText(row.id, "discountRate", updateRow)}
                      className={`${cellInput} ${cellInputMono} ${cellInputRight}`}
                    />
                  </td>
                  <td className="px-1 py-2">
                    <Select
                      value={row.taxRate}
                      onValueChange={(v) => updateRow(row.id, { taxRate: v })}
                    >
                      <SelectTrigger className="h-8 text-sm" aria-label={`GST slab ${row.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {GST_OPTIONS.map((opt) => (
                          <SelectItem key={opt} value={opt}>
                            {opt}%
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-sm text-ink">
                    {paiseToInrExact(computeLineSubtotalPaise(row)).replace("₹", "")}
                  </td>
                  <td className="px-1 py-2 text-center">
                    <button
                      type="button"
                      aria-label={`Remove ${row.id}`}
                      onClick={() => removeRow(row.id)}
                      disabled={rows.length === 1}
                      className="p-1 text-muted hover:text-bad disabled:cursor-not-allowed disabled:opacity-30"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t border-line bg-raised">
              <td colSpan={9} className="px-2 py-2">
                <Button intent="ghost" size="sm" onClick={addRow}>
                  <Plus size={14} />
                  <span>Add line item</span>
                </Button>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* AI discrepancy hint — bill-only addition under the table. */}
      <div className="mt-4 flex items-start gap-3 rounded-md border border-sky-100 bg-sky-50 p-3 text-sm text-ink-2">
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-sky-700" />
        <p className="min-w-0 flex-1 leading-relaxed">
          {error ? (
            <span className="font-semibold text-bad">{error}</span>
          ) : (
            <>
              <strong className="text-ink">1 price discrepancy vs PO.</strong> Spring washers were
              quoted at <span className="font-mono">₹14.00</span> in PO-2026-038 but bill shows{" "}
              <span className="font-mono">₹15.00</span> — that's{" "}
              <span className="font-mono">+₹800</span> on this line. Either accept it, or hold for
              review.
            </>
          )}
        </p>
        <Button intent="ghost" size="sm" className="border border-line bg-paper">
          Hold
        </Button>
      </div>
    </SectionCard>
  );
}

// ── change handler factory ──────────────────────────────────────────

/**
 * Hydrate five fields of a line in one click when the picker fires.
 * Empty hsn/rate from the picker (item had no default) are still applied
 * so the operator's old hand-typed value gets cleared along with the swap
 * — matches the invoice composer's behaviour (`ItemPicker` always wins).
 */
function applyPickedItem(
  id: string,
  values: PickedBillItemValues,
  updateRow: (id: string, patch: Partial<BillLineItem>) => void,
) {
  updateRow(id, {
    description: values.description,
    hsn: values.hsn,
    unit: values.unit,
    rate: values.rate,
    taxRate: values.taxRate,
  });
}

function onText(
  id: string,
  field: keyof BillLineItem,
  updateRow: (id: string, patch: Partial<BillLineItem>) => void,
  opts?: { normalize?: (v: string) => string },
) {
  return (event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    const value = opts?.normalize ? opts.normalize(raw) : raw;
    updateRow(id, { [field]: value } as Partial<BillLineItem>);
  };
}

function newEmptyRow(index: number): BillLineItem {
  return {
    id: `line-${index}`,
    description: "",
    hsn: "",
    qty: "1",
    unit: "pcs",
    rate: "",
    discountRate: "0",
    taxRate: "18",
  };
}
